import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "claimops-token-v1";
const USER_KEY = "claimops-user-v1";
const LOCAL_KEY_PREFIX = "claimops-va-v02";  // bumped to invalidate single-tenant caches

// ---- Token management ----
export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}
function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (err) { console.error("token store", err); }
}
function setStoredUser(user) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch (err) { console.error("user store", err); }
}
export function getStoredUser() {
  try { const raw = localStorage.getItem(USER_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

// Per-user localStorage cache so user A never sees user B's cached data
function userScopedKey(workspaceId) {
  const u = getStoredUser();
  const uid = u?.id || "anon";
  return `${LOCAL_KEY_PREFIX}-${uid}-${workspaceId}`;
}

function readLocal(workspaceId) {
  try { const raw = localStorage.getItem(userScopedKey(workspaceId)); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function writeLocal(data, workspaceId) {
  try { localStorage.setItem(userScopedKey(workspaceId), JSON.stringify(data)); } catch (err) { console.error("ws cache", err); }
}

// ---- Axios instance with auth header ----
export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Centralized 401 handler: clear token and redirect-via-event so AuthProvider can react
let onUnauthorized = null;
export function registerUnauthorizedHandler(fn) { onUnauthorized = fn; }
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      setToken("");
      setStoredUser(null);
      if (onUnauthorized) onUnauthorized();
    }
    return Promise.reject(err);
  },
);

// ---- Auth API ----
export async function authLogin(email, password) {
  const res = await api.post("/auth/login", { email, password });
  setToken(res.data.token);
  setStoredUser(res.data.user);
  return res.data.user;
}
export async function authRegister(email, password) {
  const res = await api.post("/auth/register", { email, password });
  setToken(res.data.token);
  setStoredUser(res.data.user);
  return res.data.user;
}
export async function authMe() {
  const res = await api.get("/auth/me");
  setStoredUser(res.data.user);
  return res.data.user;
}
export async function authLogout() {
  try { await api.post("/auth/logout"); } catch (err) { console.error("logout", err); }
  setToken("");
  setStoredUser(null);
  // Clear any user-scoped caches
  try {
    Object.keys(localStorage).forEach((k) => { if (k.startsWith(LOCAL_KEY_PREFIX)) localStorage.removeItem(k); });
  } catch (err) { console.error("clear cache", err); }
}

// Guest detection — when there's no auth token, we never call the API; we
// just read/write the per-tab "anon" localStorage cache so guests get the
// full app surface without any silent 401s in the network panel.
export function isGuest() { return !getToken(); }

// ---- Claim Data ----
export async function loadClaimData(workspaceId = "default") {
  if (isGuest()) {
    return readLocal(workspaceId) || null;
  }
  try {
    const res = await api.get(`/claim-data`, { params: { workspaceId } });
    const data = res.data;
    if (data && (data.issues?.length || data.workspace?.title)) {
      writeLocal(data, workspaceId);
      return data;
    }
    return readLocal(workspaceId) || data;
  } catch (err) {
    if (err.response?.status === 401) return null;
    console.error("Failed to load claim data:", err);
    return readLocal(workspaceId) || null;
  }
}
export async function saveClaimData(data, workspaceId = "default") {
  writeLocal(data, workspaceId);
  if (isGuest()) return; // guests never hit the backend
  try { await api.put(`/claim-data`, data, { params: { workspaceId } }); }
  catch (err) { if (err.response?.status !== 401) console.warn("Backend save failed:", err); }
}

// ---- Workspaces ----
export async function listWorkspaces() {
  try { const res = await api.get(`/workspaces`); return res.data; }
  catch (err) { if (err.response?.status !== 401) console.error("list workspaces:", err); return []; }
}
export async function createWorkspace(title, claimType) {
  try { const res = await api.post(`/workspaces`, { title, claimType }); return res.data; }
  catch (err) {
    if (err.response?.status === 402) {
      const e = new Error(err.response?.data?.detail || "Upgrade required");
      e.code = 402;
      throw e;
    }
    console.error("create workspace:", err);
    return null;
  }
}
export async function deleteWorkspace(wsId) {
  try { await api.delete(`/workspaces/${wsId}`); return true; }
  catch (err) { console.error("delete workspace:", err); return false; }
}

// ---- Backups ----
export async function createBackup(reason, snapshot) {
  if (isGuest()) {
    // Guests can't persist backups server-side; return a stub so callers
    // (manual-save flow) don't blow up.
    return { id: `local-${Date.now()}`, reason, createdAt: new Date().toISOString(), guest: true };
  }
  try { const res = await api.post(`/backup`, { reason, snapshot }); return res.data; }
  catch (err) {
    // 402 = free-tier paywall; not an error worth shouting about.
    if (err.response?.status !== 402) console.error("create backup:", err);
    return { id: `local-${Date.now()}`, reason, createdAt: new Date().toISOString(), paywalled: err.response?.status === 402 };
  }
}
export async function listBackups() {
  try { const res = await api.get(`/backups`); return res.data; }
  catch (err) { if (err.response?.status !== 401) console.error("list backups:", err); return []; }
}
export async function restoreBackup(backupId) {
  try { const res = await api.post(`/restore/${backupId}`); return res.data; }
  catch (err) { console.error("restore backup:", err); return null; }
}

// ---- Billing API ----
export async function billingMe() {
  const res = await api.get(`/billing/me`);
  return res.data;
}
export async function billingCheckoutSession(plan) {
  const origin = window.location.origin;
  const res = await api.post(`/billing/checkout-session`, { plan, origin });
  return res.data;
}
export async function billingPortal() {
  const origin = window.location.origin;
  const res = await api.post(`/billing/portal`, { origin });
  return res.data;
}
export async function billingVerifySession(sessionId) {
  const res = await api.get(`/billing/verify-session`, { params: { session_id: sessionId } });
  return res.data;
}

// ---- Admin ----
export async function adminListUsers() {
  const res = await api.get(`/admin/users`);
  return res.data;
}
export async function adminDeleteUser(userId) {
  await api.delete(`/admin/users/${userId}`);
  return true;
}
export async function adminUpdateUser(userId, updates) {
  const res = await api.patch(`/admin/users/${userId}`, updates);
  return res.data;
}
export async function adminGrantPro(userId) {
  const res = await api.post(`/admin/users/${userId}/grant-pro`);
  return res.data;
}
export async function adminRevokePro(userId) {
  const res = await api.post(`/admin/users/${userId}/revoke-pro`);
  return res.data;
}
export async function adminResetPassword(userId, newPassword) {
  const res = await api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
  return res.data;
}
export async function adminListInvites() {
  const res = await api.get(`/admin/invites`);
  return res.data;
}
export async function adminCreateInvite({ email, grantPro = true, note = "" }) {
  const res = await api.post(`/admin/invites`, { email, grant_pro: grantPro, note });
  return res.data;
}
export async function adminRevokeInvite(inviteId) {
  await api.delete(`/admin/invites/${inviteId}`);
  return true;
}
export async function fetchInvitePreview(token) {
  const res = await api.get(`/auth/invite/${token}`);
  return res.data;
}
export async function authRegisterWithInvite(email, password, inviteToken) {
  const res = await api.post(`/auth/register`, { email, password, invite_token: inviteToken });
  setToken(res.data.token);
  setStoredUser(res.data.user);
  return res.data.user;
}

export async function authGoogleSession(sessionId) {
  const res = await api.post(`/auth/google/session`, { session_id: sessionId });
  setToken(res.data.token);
  setStoredUser(res.data.user);
  return res.data.user;
}

// ---- Email verification ----
export async function sendVerificationEmail() {
  const res = await api.post(`/auth/send-verification`);
  return res.data; // { sent, alreadyVerified, email }
}
export async function verifyEmailCode({ code, token } = {}) {
  const res = await api.post(`/auth/verify-email`, { code, token });
  if (res.data?.user) setStoredUser(res.data.user);
  return res.data;
}

// ---- Appointments ----
export async function listAppointments() {
  try { const res = await api.get(`/appointments`); return res.data; }
  catch (err) { if (err.response?.status !== 401) console.error("list appointments:", err); return []; }
}
export async function createAppointment(payload) {
  const res = await api.post(`/appointments`, payload);
  return res.data;
}
export async function updateAppointment(id, patch) {
  const res = await api.patch(`/appointments/${id}`, patch);
  return res.data;
}
export async function deleteAppointment(id) {
  await api.delete(`/appointments/${id}`);
  return true;
}

// ---- Prescriptions ----
export async function listPrescriptions() {
  try { const res = await api.get(`/prescriptions`); return res.data; }
  catch (err) { if (err.response?.status !== 401) console.error("list prescriptions:", err); return []; }
}
export async function createPrescription(payload) {
  const res = await api.post(`/prescriptions`, payload);
  return res.data;
}
export async function updatePrescription(id, patch) {
  const res = await api.patch(`/prescriptions/${id}`, patch);
  return res.data;
}
export async function deletePrescription(id) {
  await api.delete(`/prescriptions/${id}`);
  return true;
}
export async function scanPrescriptionImage(fileBlob, pharmacyHint = "") {
  const fd = new FormData();
  fd.append("file", fileBlob, fileBlob.name || "rx.jpg");
  if (pharmacyHint && pharmacyHint.trim()) fd.append("pharmacy_hint", pharmacyHint.trim());
  const res = await api.post(`/prescriptions/ocr`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 90000, // server times out at 45s; leave headroom for network + multipart upload
  });
  return res.data;
}

// ---- NDC lookup ----
// Returns { ndc, brand_name, generic_name, dosage_form, strength } on hit.
// Throws when openFDA has no record or the service is down — the caller
// surfaces the error to the user.
export async function lookupNdc(code) {
  const res = await api.get(`/ndc/lookup`, { params: { code } });
  return res.data;
}

// ---- Pharmacy patterns (learn-from-correction) ----
export async function listPharmacyPatterns() {
  try { const res = await api.get(`/pharmacy-patterns`); return res.data; }
  catch (err) { if (err.response?.status !== 401) console.error("list pharmacy patterns:", err); return []; }
}
export async function matchPharmacyPattern(pharmacy) {
  // Returns null on 404 (no learned pattern), otherwise the pattern object.
  try {
    const res = await api.get(`/pharmacy-patterns/match`, { params: { pharmacy } });
    return res.data;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}
export async function learnPharmacyPattern(pharmacyName, example) {
  const res = await api.post(`/pharmacy-patterns/learn`, { pharmacyName, example });
  return res.data;
}

// ---- Symptoms ----
export async function listSymptoms() {
  try { const res = await api.get(`/symptoms`); return res.data; }
  catch (err) { if (err.response?.status !== 401) console.error("list symptoms:", err); return []; }
}
export async function createSymptom(payload) {
  const res = await api.post(`/symptoms`, payload);
  return res.data;
}
export async function updateSymptom(id, patch) {
  const res = await api.patch(`/symptoms/${id}`, patch);
  return res.data;
}
export async function deleteSymptom(id) {
  await api.delete(`/symptoms/${id}`);
  return true;
}
export async function transcribeSymptomAudio(blob, filename = "voice-note.webm") {
  const fd = new FormData();
  fd.append("file", blob, filename);
  const res = await api.post(`/symptoms/transcribe`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 90000,
  });
  return res.data; // { text }
}
export async function parseVoiceEntry(text) {
  const res = await api.post(`/symptoms/voice-parse`, { text }, { timeout: 60000 });
  return res.data; // { fields, transcript }
}

// ---- Episodes (Ongoing Symptom Episodes — Jun 2026) ----
export async function listEpisodes(status) {
  try {
    const res = await api.get(`/episodes${status ? `?status=${status}` : ""}`);
    return res.data;
  } catch (err) {
    if (err.response?.status !== 401) console.error("list episodes:", err);
    return [];
  }
}
export async function startEpisode(payload) {
  const res = await api.post(`/episodes`, payload);
  return res.data; // { episode, day }
}
export async function getEpisode(id) {
  const res = await api.get(`/episodes/${id}`);
  return res.data; // { episode, days }
}
export async function patchEpisode(id, patch) {
  const res = await api.patch(`/episodes/${id}`, patch);
  return res.data;
}
export async function addEpisodeDay(id, payload) {
  const res = await api.post(`/episodes/${id}/days`, payload);
  return res.data; // { day, episode, dayNumber }
}
export async function patchEpisodeDay(episodeId, entryId, patch) {
  const res = await api.patch(`/episodes/${episodeId}/days/${entryId}`, patch);
  return res.data;
}
export async function endEpisode(id, body = {}) {
  const res = await api.post(`/episodes/${id}/end`, body);
  return res.data;
}
export async function reopenEpisode(id) {
  const res = await api.post(`/episodes/${id}/reopen`);
  return res.data;
}
export async function deleteEpisode(id, { cascade = false } = {}) {
  await api.delete(`/episodes/${id}${cascade ? "?cascade=true" : ""}`);
  return true;
}
export async function promoteSymptomToEpisode(symptomId) {
  const res = await api.post(`/symptoms/${symptomId}/promote-to-episode`);
  return res.data; // { episode, day }
}

// ---- Condition Suggestions ----
export async function submitConditionSuggestion(payload) {
  try {
    const res = await api.post(`/condition-suggestions`, payload);
    return res.data;
  } catch (err) {
    if (err.response?.status !== 401) console.error("submit suggestion:", err);
    return null;
  }
}

export async function adminListConditionSuggestions() {
  try { const res = await api.get(`/admin/condition-suggestions`); return res.data; }
  catch (err) { console.error("admin list suggestions:", err); return []; }
}

export async function adminUpdateConditionSuggestion(id, patch) {
  const res = await api.patch(`/admin/condition-suggestions/${id}`, patch);
  return res.data;
}

export async function adminDeleteConditionSuggestion(id) {
  await api.delete(`/admin/condition-suggestions/${id}`);
  return true;
}

// ---- Calendar (.ics) ----
export async function getCalendarToken() {
  try { const res = await api.get(`/calendar/token`); return res.data; }
  catch (err) { console.error("get calendar token:", err); return null; }
}
export async function rotateCalendarToken() {
  const res = await api.post(`/calendar/token/rotate`);
  return res.data;
}
export async function revokeCalendarToken() {
  await api.delete(`/calendar/token`);
  return true;
}
export async function getCalendarPreview(limit = 5) {
  const res = await api.get(`/calendar/preview`, { params: { limit } });
  return res.data;
}
export function getCalendarFeedUrl(token) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base.replace(/\/$/, "")}/api/calendar/feed/${encodeURIComponent(token)}.ics`;
}
export function getAppointmentIcsUrl(appointmentId) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base.replace(/\/$/, "")}/api/calendar/appointment/${encodeURIComponent(appointmentId)}.ics`;
}
export function getPrescriptionIcsUrl(rxId) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base.replace(/\/$/, "")}/api/calendar/prescription/${encodeURIComponent(rxId)}.ics`;
}
export async function downloadIcs(url, filename) {
  const token = localStorage.getItem("token");
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const obj = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = obj;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(obj);
}

// ---- Share Links ----
export async function listShareLinks() {
  try { const res = await api.get(`/share/links`); return res.data; }
  catch (err) { if (err.response?.status !== 401) console.error("list share links:", err); return []; }
}
export async function createShareLink({ label, expiresAt }) {
  const res = await api.post(`/share/links`, { label, expiresAt: expiresAt || "" });
  return res.data;
}
export async function revokeShareLink(id) {
  await api.delete(`/share/links/${id}`);
  return true;
}
// Public — no auth header. Use plain axios to avoid 401 redirect on the global interceptor.
export async function fetchPublicShare(token) {
  const url = `${API}/share/public/${token}`;
  const res = await axios.get(url);
  return res.data;
}

// ---- Problem Reports ----
// Works for both authed users and guests (the bearer token is optional).
export async function submitProblemReport({ title, description, view, appVersion }) {
  const payload = {
    title,
    description,
    context: {
      view: view || null,
      url: typeof window !== "undefined" ? window.location.href : null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      appVersion: appVersion || "claimops-va-feb2026",
      viewportWidth: typeof window !== "undefined" ? window.innerWidth : null,
      viewportHeight: typeof window !== "undefined" ? window.innerHeight : null,
    },
  };
  const res = await api.post(`/problem-reports`, payload);
  return res.data;
}

// ---- Password reset (public, self-serve) ----
export async function requestPasswordReset(email) {
  // Always 200 with a generic message — we never leak whether the email exists.
  const res = await api.post(`/auth/password-reset/request`, { email });
  return res.data;
}
export async function confirmPasswordReset(token, newPassword) {
  const res = await api.post(`/auth/password-reset/confirm`, { token, newPassword });
  return res.data;
}

// ---- Newsletter ----
// Public single-opt-in subscribe (works for guests and authed users).
export async function subscribeNewsletter(email, source = "sidebar-pin") {
  const res = await api.post(`/newsletter/subscribe`, { email, source });
  return res.data;
}
export async function adminListNewsletterSubscribers() {
  const res = await api.get(`/admin/newsletter/subscribers`);
  return res.data;
}
export async function adminDeleteNewsletterSubscriber(id) {
  const res = await api.delete(`/admin/newsletter/subscribers/${id}`);
  return res.data;
}
// Returns the absolute URL the admin can hit (with auth header attached
// automatically because it goes through the same axios instance via fetch).
export function adminNewsletterExportUrl() {
  return `${API}/admin/newsletter/export.csv`;
}

// Statement draft-action — LLM-powered rewrites/critique for Personal Statements.
// Returns { action, output }. See backend/routers/statement_actions.py.
export async function statementDraftAction({ action, statementContext, currentText }) {
  const res = await api.post(`/statements/draft-action`, {
    action,
    statement_context: statementContext || "",
    current_text: currentText || "",
  });
  return res.data;
}

